import { useState, useEffect, useCallback, Fragment, useMemo } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

export default function ResultadosQuizzesPage() {
  const { dark } = useTheme();

  const [resultados, setResultados] = useState([]);
  const [loadingResultados, setLoadingResultados] = useState(true);

  // Filtros
  const [filterNome, setFilterNome] = useState("");
  const [filterTitulo, setFilterTitulo] = useState("");
  const [filterData, setFilterData] = useState("");

  const [expandedId, setExpandedId] = useState(null);
  const [detalhes, setDetalhes] = useState([]);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");

  // ══════════ Tokens de cor ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        success: "#16a34a",
        successBg: "#f0fdf4",
        warning: "#d97706",
        warningBg: "#fffbeb",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
      };

  // ── Carregar lista de questionários realizados ──
  const loadResultados = useCallback(async () => {
    try {
      const res = await Api.getQuestionarioResultado();
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setResultados(data);
    } catch {
      setErrorMsg("Erro ao carregar resultados dos questionários.");
    } finally {
      setLoadingResultados(false);
    }
  }, []);

  useEffect(() => {
    loadResultados();
  }, [loadResultados]);

  // ── Filtragem client-side ──
  const filteredResultados = useMemo(() => {
    return resultados.filter((r) => {
      const matchNome = filterNome.trim() === "" || (r.nome || "").toLowerCase().includes(filterNome.trim().toLowerCase());
      const matchTitulo = filterTitulo.trim() === "" || (r.titulo || "").toLowerCase().includes(filterTitulo.trim().toLowerCase());
      let matchData = true;
      if (filterData) {
        // data_fim vem como "YYYY-MM-DD HH:mm:ss" – comparamos a parte da data
        const dataFim = (r.data_fim || "").slice(0, 10);
        matchData = dataFim === filterData;
      }
      return matchNome && matchTitulo && matchData;
    });
  }, [resultados, filterNome, filterTitulo, filterData]);

  // ── Expandir / recolher ──
  const toggleExpand = (idQuestionario) => {
    if (expandedId === idQuestionario) {
      setExpandedId(null);
      setDetalhes([]);
    } else {
      setExpandedId(idQuestionario);
      setDetalhes([]);
    }
  };

  // ── Carregar detalhes de um questionário ──
  const loadDetalhes = useCallback(async (idQuestionario) => {
    setLoadingDetalhes(true);
    try {
      const resPR = await Api.getPerguntasRespostas({id_questionario:idQuestionario});
      const perguntasRespostas = Array.isArray(resPR?.data)
        ? resPR.data
        : Array.isArray(resPR)
        ? resPR
        : [];

      const detalhesData = await Promise.all(
        perguntasRespostas.map(async (item) => {
          const resAlt = await Api.getResposta({ id_pergunta: item.id_pergunta });
          const alternativas = Array.isArray(resAlt?.data)
            ? resAlt.data
            : Array.isArray(resAlt)
            ? resAlt
            : [];

          const alternativaCorreta = alternativas.find(
            (a) => String(a.correta).toLowerCase() === "true" || a.correta === true
          );

          return {
            id_pergunta: item.id_pergunta,
            pergunta: item.pergunta,
            respostaUsuarioId: Number(item.id_resposta),
            respostaUsuarioTexto: item.resposta,
            usuarioAcertou: String(item.correta).toLowerCase() === "true",
            alternativas,
            alternativaCorretaId: alternativaCorreta ? Number(alternativaCorreta.id) : null,
          };
        })
      );

      setDetalhes(detalhesData);
    } catch {
      setDetalhes([]);
      setErrorMsg("Erro ao carregar detalhes do questionário.");
    } finally {
      setLoadingDetalhes(false);
    }
  }, []);

  useEffect(() => {
    if (expandedId) {
      loadDetalhes(expandedId);
    }
  }, [expandedId, loadDetalhes]);

  const formatPercent = (valor) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return "0%";
    return num.toFixed(0) + "%";
  };

  const clearFilters = () => {
    setFilterNome("");
    setFilterTitulo("");
    setFilterData("");
  };

  // ═══════════════ Estilos ═══════════════
  const styles = `
    .rq-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .rq-container { max-width: 1200px; margin: 0 auto; }
    .rq-header { margin-bottom: 32px; }
    .rq-heading { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; }
    .rq-sub { font-size: 0.88rem; color: ${t.textMuted}; margin-top: 4px; }

    .rq-filters {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
    }
    .rq-filter-field label {
      display: block;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: ${t.textMuted};
      margin-bottom: 6px;
    }
    .rq-filter-field input {
      width: 100%;
      height: 38px;
      padding: 0 12px;
      border: 1px solid ${t.border};
      border-radius: 10px;
      background: ${t.bgCard};
      color: ${t.textPrimary};
      font-size: 0.85rem;
      outline: none;
      transition: 0.2s;
    }
    .rq-filter-field input:focus {
      border-color: ${t.borderActive};
      box-shadow: 0 0 0 3px ${t.bgActive};
    }
    .rq-filter-actions {
      display: flex;
      align-items: flex-end;
      gap: 8px;
    }
    .rq-filter-btn {
      height: 38px;
      padding: 0 16px;
      border: 1px solid ${t.border};
      border-radius: 10px;
      background: ${t.bgCard};
      color: ${t.textSecondary};
      font-weight: 600;
      font-size: 0.82rem;
      cursor: pointer;
      transition: 0.2s;
      white-space: nowrap;
    }
    .rq-filter-btn:hover {
      background: ${t.bgHover};
      color: ${t.textPrimary};
      border-color: ${t.borderActive};
    }

    .rq-section-title { font-size: 1rem; font-weight: 700; color: ${t.textPrimary}; margin-bottom: 16px; }
    .rq-table-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; overflow: hidden; }
    .rq-table { width: 100%; border-collapse: collapse; }
    .rq-table thead th {
      text-align: left; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
      color: ${t.textMuted}; padding: 14px 22px; background: ${t.bgHover}; border-bottom: 1px solid ${t.border};
    }
    .rq-table tbody td { padding: 14px 22px; font-size: 0.88rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; vertical-align: middle; }
    .rq-table tbody tr:last-child td { border-bottom: none; }
    .rq-table tbody tr:hover { background: ${t.rowHover}; }
    .rq-table tbody tr.expanded { background: ${t.bgHover}; }

    .rq-expand-icon {
      display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px;
      border-radius: 8px; cursor: pointer; transition: 0.2s; color: ${t.textSecondary};
    }
    .rq-expand-icon:hover { background: ${t.bgActive}; color: ${t.textActive}; }

    .rq-badge {
      display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px;
      font-size: 0.72rem; font-weight: 700;
    }
    .rq-badge.green { background: ${t.successBg}; color: ${t.success}; }
    .rq-badge.red { background: ${t.dangerBg}; color: ${t.danger}; }
    .rq-badge.yellow { background: ${t.warningBg}; color: ${t.warning}; }

    .rq-detail-row td { padding: 0; border-bottom: 1px solid ${t.border}; }
    .rq-detail-container { padding: 16px 22px 20px; border-top: 1px solid ${t.border}; }

    .rq-question-title { font-size: 0.9rem; font-weight: 600; color: ${t.textPrimary}; margin-top: 20px; margin-bottom: 10px; }
    .rq-answer-list { display: flex; flex-direction: column; gap: 6px; padding-left: 16px; }
    .rq-answer-item { display: flex; align-items: center; gap: 8px; font-size: 0.86rem; color: ${t.textPrimary}; }
    .rq-answer-marker {
      width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    }
    .rq-answer-marker.correct { background: ${t.success}; }
    .rq-answer-marker.incorrect { background: ${t.danger}; }
    .rq-answer-marker.neutral { background: ${t.border}; }
    .rq-answer-text.correct { color: ${t.success}; font-weight: 600; }
    .rq-answer-text.incorrect { color: ${t.danger}; font-weight: 600; }
    .rq-answer-text.neutral { color: ${t.textSecondary}; }
  `;

  return (
    <div className="rq-page">
      <style>{styles}</style>
      <div className="rq-container">
        <div className="rq-header">
          <h1 className="rq-heading">Resultados dos Questionários</h1>
          <p className="rq-sub">Visualize o desempenho de cada utilizador nos quizzes</p>
        </div>

        {errorMsg && (
          <div style={{ background: t.dangerBg, color: t.danger, padding: "14px 20px", borderRadius: 14, marginBottom: 24 }}>
            {errorMsg}
          </div>
        )}

        {/* Filtros */}
        <div className="rq-filters">
          <div className="rq-filter-field">
            <label>Nome do usuário</label>
            <input
              type="text"
              placeholder="Pesquisar por nome..."
              value={filterNome}
              onChange={(e) => setFilterNome(e.target.value)}
            />
          </div>
          <div className="rq-filter-field">
            <label>Título da formação</label>
            <input
              type="text"
              placeholder="Pesquisar por título..."
              value={filterTitulo}
              onChange={(e) => setFilterTitulo(e.target.value)}
            />
          </div>
          <div className="rq-filter-field">
            <label>Data (fim)</label>
            <input
              type="date"
              value={filterData}
              onChange={(e) => setFilterData(e.target.value)}
            />
          </div>
          <div className="rq-filter-actions">
            <button className="rq-filter-btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>
        </div>

        <div className="rq-section-title">Questionários realizados</div>

        {loadingResultados ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Carregando...</div>
        ) : filteredResultados.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>
            Nenhum questionário encontrado com os filtros aplicados.
          </div>
        ) : (
          <div className="rq-table-card">
            <table className="rq-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Utilizador</th>
                  <th>Data Fim</th>
                  <th>Total</th>
                  <th>Certas</th>
                  <th>Erradas</th>
                  <th>% Acerto</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredResultados.map((r) => {
                  const total = Number(r.total_respostas) || 0;
                  const certas = Number(r.respostas_corretas) || 0;
                  const erradas = Number(r.respostas_erradas) || 0;
                  const percent = total > 0 ? (certas / total) * 100 : 0;

                  return (
                    <Fragment key={r.id_questionario}>
                      <tr className={expandedId === r.id_questionario ? "expanded" : ""}>
                        <td style={{ fontWeight: 600 }}>{r.titulo || "—"}</td>
                        <td>{r.nome || "—"}</td>
                        <td>{r.data_fim ? new Date(r.data_fim).toLocaleString("pt-PT") : "—"}</td>
                        <td>{total}</td>
                        <td><span className="rq-badge green">{certas}</span></td>
                        <td><span className="rq-badge red">{erradas}</span></td>
                        <td>
                          <span className={`rq-badge ${percent >= 70 ? 'green' : percent >= 40 ? 'yellow' : 'red'}`}>
                            {formatPercent(percent)}
                          </span>
                        </td>
                        <td>
                          <span
                            className="rq-expand-icon"
                            onClick={() => toggleExpand(r.id_questionario)}
                          >
                            <svg
                              width="18" height="18" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ transform: expandedId === r.id_questionario ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </span>
                        </td>
                      </tr>

                      {expandedId === r.id_questionario && (
                        <tr className="rq-detail-row">
                          <td colSpan={8}>
                            <div className="rq-detail-container">
                              {loadingDetalhes ? (
                                <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>
                                  Carregando perguntas e respostas...
                                </div>
                              ) : detalhes.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>
                                  Nenhum detalhe disponível.
                                </div>
                              ) : (
                                detalhes.map((d) => (
                                  <div key={d.id_pergunta}>
                                    <div className="rq-question-title">{d.pergunta}</div>
                                    <div className="rq-answer-list">
                                      {d.alternativas.map((alt) => {
                                        const isCorrect = Number(alt.id) === d.alternativaCorretaId;
                                        const isUserChoice = Number(alt.id) === d.respostaUsuarioId;
                                        const userErrou = isUserChoice && !d.usuarioAcertou;
                                        const userAcertou = isUserChoice && d.usuarioAcertou;

                                        let markerClass = "neutral";
                                        let textClass = "neutral";
                                        if (isCorrect || userAcertou) {
                                          markerClass = "correct";
                                          textClass = "correct";
                                        } else if (userErrou) {
                                          markerClass = "incorrect";
                                          textClass = "incorrect";
                                        }

                                        return (
                                          <div key={alt.id} className="rq-answer-item">
                                            <span className={`rq-answer-marker ${markerClass}`} />
                                            <span className={`rq-answer-text ${textClass}`}>
                                              {alt.resposta}
                                              {isCorrect && " ✓"}
                                              {userErrou && " (sua resposta)"}
                                              {userAcertou && " (sua resposta ✓)"}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}